import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { PlatformIcon, type PlatformId } from './PlatformIcon';
import { PLATFORM_LIST } from './platform-list';
import { Icons } from '@/shared/constants/icons';
import type { ChannelHealthState } from '@/shared/lib/channel-health';
import { platformColorVar } from '@/shared/lib/platform-color';

export type PlatformMarkSize = 16 | 20 | 24;

interface PlatformMarkProps {
  platform: PlatformId;
  size?: PlatformMarkSize;
  /** Draws the status ring around the mark. Omit it for a plain brand mark. */
  state?: ChannelHealthState;
  /** Multi-select: a check badge on the mark. */
  selected?: boolean;
  /** Fades the mark, for a channel that is not connected. */
  dimmed?: boolean;
  /** Overrides the platform name in the accessible label (the brand name by default). */
  label?: string;
  /** Pulses the ring once when it appears (an expiring channel on the overview); off under reduced motion. */
  pulseOnce?: boolean;
  className?: string;
}

const RING: Record<ChannelHealthState, string> = {
  healthy: 'var(--good)',
  expiring: 'var(--warn)',
  expired: 'var(--bad)',
  disconnected: 'var(--line-2)',
  pending: 'var(--ink-3)',
};

const ICON_SIZE: Record<PlatformMarkSize, number> = { 16: 9, 20: 11, 24: 13 };
const BADGE_SIZE: Record<PlatformMarkSize, number> = { 16: 9, 20: 10, 24: 12 };

function platformName(platform: PlatformId): string {
  return PLATFORM_LIST.find((p) => p.id === platform)?.label ?? platform;
}

/**
 * C16 · Platform mark. A brand-colour circle holding the platform icon, in
 * three sizes. The ring says how the connection is doing (good, warn, bad,
 * neutral for not connected, ink for pending), the check badge marks a
 * selection, and `dimmed` fades a channel that is not connected. The
 * accessible name is the platform plus its state.
 */
export function PlatformMark({ platform, size = 20, state, selected = false, dimmed = false, label, pulseOnce = false, className = '' }: PlatformMarkProps): JSX.Element {
  const { t } = useTranslation();
  const name = label ?? platformName(platform);
  const base = state ? t(`channels.mark.${state}`, { platform: name }) : name;
  const ariaLabel = selected ? t('channels.mark.selected', { label: base }) : base;

  const style: CSSProperties = {
    width: size,
    height: size,
    background: platformColorVar(platform),
    opacity: dimmed ? 0.4 : 1,
    boxShadow: state ? `0 0 0 1px var(--color-surface), 0 0 0 3px ${RING[state]}` : undefined,
  };

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      data-state={state}
      className={['relative inline-flex shrink-0 items-center justify-center rounded-full align-middle', className].join(' ')}
      style={style}
    >
      <PlatformIcon platform={platform} size={ICON_SIZE[size]} fill="var(--color-surface)" aria-hidden="true" />
      {pulseOnce && state && <span className="ring-pulse-once" style={{ color: RING[state] }} data-testid="ring-pulse" aria-hidden="true" />}
      {selected && (
        <span
          data-testid="platform-mark-check"
          className="absolute flex items-center justify-center rounded-full"
          style={{
            width: BADGE_SIZE[size],
            height: BADGE_SIZE[size],
            insetInlineEnd: -2,
            bottom: -2,
            background: 'var(--color-accent)',
            color: 'var(--color-surface)',
            boxShadow: '0 0 0 1.5px var(--color-surface)',
          }}
        >
          <Icons.check size={BADGE_SIZE[size] - 3} strokeWidth={3} aria-hidden="true" />
        </span>
      )}
    </span>
  );
}
